<script lang="ts">
import type { InputTimeProps } from '@nuxt/ui';
import type { PrimitiveType } from '#layers/data-model/public';
import { PRIMITIVE_TYPE_TIME } from '#layers/data-model/public';

export const supportedTimeFieldTypes = ({ primitiveType }: { primitiveType: PrimitiveType }) =>
	primitiveType.name === PRIMITIVE_TYPE_TIME ? ['datetime'] : [];

const __DsBlock = {
	type: 'input',
	icon: 'lucide:clock',
	name: '$t:blocks.input_time.name',
	description: '$t:blocks.input_time.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: supportedTimeFieldTypes,
		recommendedDisplays: ['$builtin:DisplayFormattedTime'],
	},

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
	],
}</script>

<script lang="ts" setup>
import type { Temporal } from 'temporal-polyfill';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { timeConverter } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends InputBlockProps, Pick<InputTimeProps, 'variant' | 'size' | 'disabled'> {
	block?: boolean;
	// Absorbs the shared placeholder ("NULL") DefaultValueInput passes to every input block; time inputs don't render it.
	placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

const forwarded = reactivePick(props, 'variant', 'size', 'readonly', 'block');

const model = defineModel<Temporal.PlainTime | null>();

const { modelValue: m, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

;

const timeModel = computed({
	get: () => timeConverter.toCalendar(m.value),
	set: (value) => {
		m.value = timeConverter.toTemporal(value);
	},
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInputTime
			v-bind="forwarded"
			v-model="timeModel"
			class="w-full min-w-0 gap-0.5 overflow-hidden"
			:disabled
			:aria-readonly="readonly"
		/>
	</DTooltip>
</template>
